import React, { useEffect, useState } from "react";
import { Backdrop, Box, CircularProgress, CssBaseline, Grid } from "@mui/material";
import { useLocation, useNavigate } from "react-router-dom";
import DetailsPageHeader from "../../../../../common/DetailsPageHeader";
import GeneralAdminSidebar from "../../../../../common/GeneralAdminSidebar";
import TableSearchBar from "../../../../../common/TableSearchBar";
import CommonTable from "../../../../../common/CommonTable";
import {
  drawerWidth,
  GeneralAdminRoutes,
  mainLayoutStyles,
  showNotification,
} from "../../../../../constants";
import { useAuth } from "../../../../../hooks";
import { getAllUsers } from "../../../GAUsers/hooks/GAUsersHooks";
import { updateGroup } from "../../hooks/GAGroupsHooks";

const formatSignUpDate = (createdAt) => {
  if (!createdAt) return "";
  // Firestore Timestamp: { seconds, nanoseconds }
  const d = createdAt?.seconds ? new Date(createdAt.seconds * 1000) : new Date(createdAt);
  if (Number.isNaN(d.getTime())) return "";
  const mm = String(d.getMonth() + 1).padStart(2, "0");
  const dd = String(d.getDate()).padStart(2, "0");
  const yyyy = d.getFullYear();
  return `${mm}/${dd}/${yyyy}`;
};

const usersHeadCells = [
  { id: "firstName", numeric: false, disablePadding: true, label: "First Name", width: 200 },
  { id: "lastName", numeric: false, disablePadding: true, label: "Last Name", width: 200 },
  { id: "userName", numeric: false, disablePadding: true, label: "Username", width: 220 },
  { id: "createdAt", numeric: false, disablePadding: true, label: "Sign Up Date", width: 160 },
  { id: "id", numeric: false, disablePadding: true, label: "User ID", width: 260 },
];

const GAAddMembersToGroupContainer = () => {
  const { currentUser } = useAuth();
  const location = useLocation();
  const navigate = useNavigate();

  const [loading, setLoading] = useState(false);
  const [groupDetails, setGroupDetails] = useState(location?.state?.groupDetails);
  const [users, setUsers] = useState([]);
  const [selected, setSelected] = useState([]);

  useEffect(() => {
    if (!location?.state?.groupDetails?.id) {
      navigate(GeneralAdminRoutes.groups, { replace: true });
      return;
    }
    setGroupDetails(location.state.groupDetails);
    loadUsers();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location.state]);

  const loadUsers = async () => {
    try {
      setLoading(true);
      const snap = await getAllUsers();
      const all = snap?.docs?.map((d) => ({ id: d.id, ...d.data() })) ?? [];
      const existing = location.state.groupDetails?.members || [];
      // Hard filter: do not allow selecting Guest users
      // (Guest accounts show up as First Name "Guest" + Last Name "User")
      const filtered = all
        .filter((u) => !existing.includes(u.id))
        .filter((u) => {
          const first = (u?.firstName || "").trim().toLowerCase();
          const last = (u?.lastName || "").trim().toLowerCase();
          return !(first === "guest" && last === "user");
        });
      setUsers(filtered);
    } catch (error) {
      console.log("error loading users:", error.message);
    } finally {
      setLoading(false);
    }
  };

  const handleSearchUsers = (e) => {
    const v = e.target.value.toLowerCase();
    if (!v) {
      loadUsers();
      return;
    }
    setUsers((prev) =>
      prev.filter((u) =>
        `${u.firstName || ""} ${u.lastName || ""} ${u.userName || ""}`
          .toLowerCase()
          .includes(v)
      )
    );
  };

  const handleSave = async () => {
    try {
      setLoading(true);
      const existing = groupDetails?.members || [];
      const updated = [...existing, ...selected];
      const unique = [...new Set(updated)];

      await updateGroup(groupDetails.id, {
        members: unique,
        updatedAt: new Date(),
        updatedBy: currentUser?.id,
      });
      setSelected([]);
      showNotification("Group members added successfully.");
      navigate(
        GeneralAdminRoutes.groupDetails,
        {
          replace: true,
          state: {
            groupDetails: { ...groupDetails, members: unique },
            activeTab: "members",
          },
        }
      );
    } catch (error) {
      console.log("error updating group members:", error.message);
    } finally {
      setLoading(false);
    }
  };

  return (
    <Box sx={{ display: "flex", width: "100%", paddingRight: 4 }}>
      <CssBaseline />
      <DetailsPageHeader
        mainTitle={`Add Members to '${groupDetails?.name || ""}'`}
        bgColor="#fff"
        selected={selected}
        setSelected={setSelected}
        editEnabled={true}
        cancelButton={true}
        loading={loading}
        submitButtonText={"Submit"}
        onCancelClick={() => {
          navigate(
            GeneralAdminRoutes.groupDetails,
            { replace: true, state: { groupDetails, activeTab: "members" } }
          );
        }}
        handleSave={handleSave}
      />
      <GeneralAdminSidebar />
      <Grid container style={{ ...mainLayoutStyles, background: "#fff" }}>
        <Grid container direction={"column"} style={subContainerStyles}>
          <TableSearchBar placeholder={"Search"} handleSearch={handleSearchUsers} />
          <CommonTable
            tableData={users}
            selected={selected}
            setSelected={setSelected}
            CommonTableRow={DummyUserRow}
            showPosition={false}
            tableHeadData={usersHeadCells}
          />
        </Grid>
      </Grid>
      <Backdrop
        sx={{ color: "#fff", zIndex: (theme) => theme.zIndex.drawer + 1 }}
        open={loading}
        onClick={() => setLoading(false)}
      >
        <CircularProgress color="inherit" />
      </Backdrop>
    </Box>
  );
};

export default GAAddMembersToGroupContainer;

const subContainerStyles = {
  paddingLeft: 30,
  paddingRight: 30,
  marginTop: 50,
};

// Minimal row renderer compatible with CommonTable
const DummyUserRow = ({
  isItemSelected,
  row,
  numberType,
  handleClickRow,
  labelId,
  provided,
}) => {
  const { Checkbox, TableCell, TableRow } = require("@mui/material");
  return (
    <TableRow
      ref={provided?.innerRef}
      {...(provided ? provided.draggableProps : {})}
      hover
      role="checkbox"
      aria-checked={isItemSelected}
      tabIndex={-1}
      key={row.id}
      selected={isItemSelected}
      sx={{
        cursor: "pointer",
        height: 48,
        background: numberType === true ? "#F2F2F2" : "#fff",
      }}
    >
      <TableCell
        padding="checkbox"
        style={{ background: "#fff" }}
        onClick={(event) => handleClickRow(event, row.id)}
      >
        <Checkbox
          color="primary"
          checked={isItemSelected}
          inputProps={{ "aria-labelledby": labelId }}
        />
      </TableCell>
      <TableCell style={{ border: "1px solid #BABABA" }}>{row?.firstName || ""}</TableCell>
      <TableCell style={{ border: "1px solid #BABABA" }}>{row?.lastName || ""}</TableCell>
      <TableCell style={{ border: "1px solid #BABABA" }}>{row?.userName || ""}</TableCell>
      <TableCell style={{ border: "1px solid #BABABA" }}>{formatSignUpDate(row?.createdAt)}</TableCell>
      <TableCell style={{ border: "1px solid #BABABA" }}>{row?.id || ""}</TableCell>
    </TableRow>
  );
};
